import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ColorPickerDialog } from "@/components/ui/color-picker-dialog";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { BRAND_AVATAR_COLORS, getContrastText, normalizeHex } from "@/lib/avatarColors";
import { cn } from "@/lib/utils";
import { Check, Plus } from "lucide-react";

export interface AvatarColorPickerProps {
  /** The normalized `#rrggbb` colour selected in the form, or null when unset. */
  value: string | null;
  onChange: (hex: string) => void;
}

/** The brand swatches plus a "custom colour" escape hatch, on one row. */
export function AvatarColorPicker({ value, onChange }: AvatarColorPickerProps) {
  const { t } = useLanguage();
  const [pickerOpen, setPickerOpen] = useState(false);

  // A colour picked in the dialog rather than one of the brand swatches — the
  // custom button previews it instead of showing the plus icon.
  const isCustom = value !== null && !BRAND_AVATAR_COLORS.some((swatch) => swatch === value);

  return (
    <div className="flex flex-wrap items-center gap-2">
      {BRAND_AVATAR_COLORS.map((swatch) => (
        <button
          key={swatch}
          type="button"
          onClick={() => onChange(swatch)}
          aria-label={swatch}
          aria-pressed={value === swatch}
          title={swatch}
          className={cn(
            "flex h-8 w-8 items-center justify-center rounded-full ring-offset-2 ring-offset-background transition focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            value === swatch && "ring-2 ring-ring"
          )}
          style={{ backgroundColor: swatch, color: getContrastText(swatch) }}
        >
          {value === swatch ? <Check className="h-4 w-4" /> : null}
        </button>
      ))}

      <Button
        id="avatarColor"
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => setPickerOpen(true)}
        aria-haspopup="dialog"
        title={t.settings.personal.pickCustomColor}
        className="gap-1.5 text-muted-foreground"
      >
        {isCustom ? (
          <span
            aria-hidden="true"
            className="h-4 w-4 shrink-0 rounded-full border border-border"
            style={{ backgroundColor: value }}
          />
        ) : (
          <Plus aria-hidden="true" className="h-4 w-4 shrink-0" />
        )}
        {t.settings.personal.customColor}
      </Button>

      <ColorPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        value={normalizeHex(value ?? "")}
        onApply={onChange}
        presetColors={BRAND_AVATAR_COLORS}
        labels={{
          title: t.settings.personal.customColor,
          description: t.settings.personal.avatarColorDescription,
          apply: t.common.select,
          cancel: t.common.cancel,
          hex: t.settings.personal.colorPickerHexLabel,
          invalidHint: t.settings.personal.invalidColor,
        }}
        placeholder={t.settings.personal.customColorPlaceholder}
      />
    </div>
  );
}
